import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { QuestionBlock } from '@core/services/ai/ai-assistant.service';

/**
 * Single open-ended follow-up question. Submitting sends the typed
 * answer as a new user message — the simplest way to nudge the model
 * down a branch when ChoiceBlock would be too restrictive.
 */
@Component({
  selector: 'app-ai-question-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <form class="qb" (submit)="$event.preventDefault(); submit()">
      <p class="qb__prompt">{{ block.prompt }}</p>
      <mat-form-field appearance="outline" class="qb__field">
        <input
          matInput
          type="text"
          [placeholder]="block.placeholder || ''"
          [(ngModel)]="answer"
          name="answer"
          autocomplete="off" />
        @if (block.helperText) {
          <mat-hint>{{ block.helperText }}</mat-hint>
        }
      </mat-form-field>
      <div class="qb__actions">
        <button
          type="submit"
          mat-flat-button
          color="primary"
          [disabled]="!hasAnswer()">
          {{ block.submitLabel || label('aiAssistant.widgets.send', 'Send') }}
        </button>
      </div>
    </form>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .qb {
        background: #fff;
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 12px;
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .qb__prompt {
        margin: 0 0 6px;
        font-size: 14px;
        line-height: 1.45;
        color: rgba(15, 23, 42, 0.85);
      }
      .qb__field {
        width: 100%;
      }
      .qb__actions {
        display: flex;
        justify-content: flex-end;
      }
    `
  ]
})
export class AiQuestionBlockComponent {
  @Input({ required: true }) block!: QuestionBlock;
  @Output() readonly answered = new EventEmitter<string>();

  answer = '';

  private readonly translate = inject(TranslateService, { optional: true });

  /** Translated label; falls back to English when no translation service/key is available. */
  protected label(key: string, fallback: string): string {
    const v = this.translate?.instant(key);
    return v && v !== key ? v : fallback;
  }

  private readonly _hasAnswer = signal(false);
  hasAnswer(): boolean {
    return this.answer.trim().length > 0;
  }

  submit(): void {
    const trimmed = this.answer.trim();
    if (!trimmed) return;
    this.answered.emit(trimmed);
    this.answer = '';
    this._hasAnswer.set(false);
  }
}
